/**
 * Hisobot analitikasi konverti (`GET /reports/{kind}/analytics`, spec
 * 2026-09-17 §2.3) va unga xizmat qiladigan SOF funksiyalar.
 *
 * ⚠️ **Hamma massiv MAJBURIY** — kind uchun ma'nosiz bo'lsa `[]`.
 * `.optional()`, `z.union()`, `z.lazy()`, `.transform()`, `.catch()` va
 * `.openapi()` ISHLATILMAYDI: sxema backend OpenAPI javobiga kiradi
 * (`form.ts` dagi konvertor qoidasi bilan bir xil sabab).
 */
import { z } from "zod/v4";

const totalSchema = z.object({
	key: z.string(),
	value: z.number(),
	previous: z.number().nullable(),
});

const sparkSchema = z.object({ key: z.string(), points: z.array(z.number()) });

const dailySchema = z.object({
	date: z.string(),
	primary: z.number(),
	flagged: z.number(),
});

const heatSchema = z.object({
	weekday: z.number().int(),
	hour: z.number().int(),
	count: z.number(),
});

const hourlySchema = z.object({
	hour: z.number().int(),
	count: z.number(),
	previous: z.number(),
});

const weekdaySchema = z.object({
	weekday: z.number().int(),
	count: z.number(),
	rate: z.number().nullable(),
});

const histogramSchema = z.object({
	bucket: z.string(),
	count: z.number(),
	flagged: z.number(),
});

const breakdownSchema = z.object({ key: z.string(), count: z.number() });

const topEmployeeSchema = z.object({
	employeeId: z.string(),
	employeeName: z.string(),
	total: z.number(),
	flagged: z.number(),
	avg: z.number().nullable(),
	weekly: z.array(z.number()),
});

const byDepartmentSchema = z.object({
	departmentId: z.string().nullable(),
	departmentName: z.string().nullable(),
	total: z.number(),
	flagged: z.number(),
	rate: z.number().nullable(),
	trend: z.array(z.number()),
});

const byZoneSchema = z.object({
	zoneId: z.string(),
	zoneName: z.string(),
	total: z.number(),
	flagged: z.number(),
});

const statusMixSchema = z.object({ status: z.string(), days: z.number() });

const matrixSchema = z.object({
	employeeId: z.string(),
	employeeName: z.string(),
	rate: z.number().nullable(),
	days: z.array(z.object({ date: z.string(), status: z.string() })),
});

const trendSchema = z.object({
	month: z.string(),
	rate: z.number().nullable(),
	bankRate: z.number().nullable(),
});

export const reportAnalyticsSchema = z.object({
	totals: z.array(totalSchema),
	spark: z.array(sparkSchema),
	daily: z.array(dailySchema),
	heat: z.array(heatSchema),
	hourly: z.array(hourlySchema),
	weekday: z.array(weekdaySchema),
	histogram: z.array(histogramSchema),
	breakdown: z.array(breakdownSchema),
	topEmployees: z.array(topEmployeeSchema),
	byDepartment: z.array(byDepartmentSchema),
	byZone: z.array(byZoneSchema),
	statusMix: z.array(statusMixSchema),
	matrix: z.array(matrixSchema),
	trend: z.array(trendSchema),
});

export type ReportAnalytics = z.infer<typeof reportAnalyticsSchema>;
export type ReportAnalyticsDaily = ReportAnalytics["daily"][number];

/** Bo'sh konvert. ⚠️ Har chaqiruvda YANGI obyekt — chaqiruvchilar uni to'ldiradi. */
export function emptyAnalytics(): ReportAnalytics {
	return {
		totals: [],
		spark: [],
		daily: [],
		heat: [],
		hourly: [],
		weekday: [],
		histogram: [],
		breakdown: [],
		topEmployees: [],
		byDepartment: [],
		byZone: [],
		statusMix: [],
		matrix: [],
		trend: [],
	};
}

const KUN_MS = 86_400_000;

function parseSana(sana: string): Date {
	const [y, m, d] = sana.split("-").map(Number);
	return new Date(Date.UTC(y as number, (m as number) - 1, d as number));
}

function formatSana(sana: Date): string {
	return sana.toISOString().slice(0, 10);
}

function oyOxiriKuni(yil: number, oy: number): number {
	return new Date(Date.UTC(yil, oy + 1, 0)).getUTCDate();
}

/**
 * Solishtiruv oynasi — YAGONA manba (dayjs'siz, UTC sana arifmetikasi).
 * Frontend oynani qayta hisoblamaydi, `totals[].previous` ni o'qiydi
 * (ilgarigi `summaryDerive.ts` dagi `oldingiOraliq` F2 da o'chirilgan).
 *
 * ⚠️ Oyna TO'LIQ BITTA kalendar oyi bo'lsa (`dateFrom` — oy boshi,
 * `dateTo` — AYNI oyning oxiri) oldingi TO'LIQ oy qaytadi va uzunlik
 * SAQLANMAYDI (mart → fevral 28 kun). Aks holda teng uzunlikdagi,
 * bevosita oldingi oyna (`2026-08-01..2026-09-30` ham shu tarmoqda —
 * u ikki oylik oyna).
 */
export function oldingiOyna(
	dateFrom: string,
	dateTo: string
): { dateFrom: string; dateTo: string } {
	const boshi = parseSana(dateFrom);
	const oxiri = parseSana(dateTo);

	const yil = boshi.getUTCFullYear();
	const oy = boshi.getUTCMonth();
	const toliqOy =
		oxiri.getUTCFullYear() === yil &&
		oxiri.getUTCMonth() === oy &&
		boshi.getUTCDate() === 1 &&
		oxiri.getUTCDate() === oyOxiriKuni(yil, oy);

	if (toliqOy) {
		const oldingiBoshi = new Date(Date.UTC(yil, oy - 1, 1));
		const oldingiOxiri = new Date(Date.UTC(yil, oy, 0));
		return { dateFrom: formatSana(oldingiBoshi), dateTo: formatSana(oldingiOxiri) };
	}

	const uzunlik = Math.round((oxiri.getTime() - boshi.getTime()) / KUN_MS) + 1;
	return {
		dateFrom: formatSana(new Date(boshi.getTime() - uzunlik * KUN_MS)),
		dateTo: formatSana(new Date(boshi.getTime() - KUN_MS)),
	};
}

/**
 * Qiymatni bucket yorlig'iga aylantiradi. `edges` qat'iy o'suvchi,
 * `labels.length === edges.length + 1`. Oraliq YARIM OCHIQ:
 * `value < edges[0]` → `labels[0]`, `edges[i-1] <= value < edges[i]` →
 * `labels[i]`, `value >= edges.at(-1)` → oxirgi yorliq.
 */
export function bucketLabel(
	value: number,
	edges: readonly number[],
	labels: readonly string[]
): string {
	for (let i = 0; i < edges.length; i++) {
		if (value < (edges[i] as number)) {
			return labels[i] as string;
		}
	}
	return labels[edges.length] as string;
}

export type BucketSet = { readonly edges: readonly number[]; readonly labels: readonly string[] };

/** Chiqish davomiyligi — DAQIQA. */
export const DURATION_BUCKETS: BucketSet = {
	edges: [5, 15, 30, 45, 60, 120],
	labels: ["0-5", "5-15", "15-30", "30-45", "45-60", "60-120", "120+"],
};

/**
 * Kechikish — butun DAQIQA. Yorliqlar YOPIQ butun oraliqlar
 * (`31-60` 60 ni ham o'z ichiga oladi), shuning uchun `60+` = `> 60`
 * va chegara `61`.
 */
export const LATE_BUCKETS: BucketSet = {
	edges: [6, 16, 31, 61],
	labels: ["1-5", "6-15", "16-30", "31-60", "60+"],
};

/** Ishlangan vaqt — ⚠️ SOAT birligida (SQL daqiqani `/ 60` qilib beradi). */
export const WORKED_HOUR_BUCKETS: BucketSet = {
	edges: [4, 6, 8, 9],
	labels: ["<4", "4-6", "6-8", "8-9", "9+"],
};

const KELISH_BOSHI = 8 * 60;
const KELISH_OXIRI = 10 * 60;
const KELISH_QADAMI = 15;

function soatMatni(daqiqa: number): string {
	const s = String(Math.floor(daqiqa / 60)).padStart(2, "0");
	const d = String(daqiqa % 60).padStart(2, "0");
	return `${s}:${d}`;
}

const kelishChegaralari: number[] = [];
for (let d = KELISH_BOSHI; d <= KELISH_OXIRI; d += KELISH_QADAMI) {
	kelishChegaralari.push(d);
}

/**
 * Kelish vaqti — ⚠️ `settings.timezone` dagi YARIM TUNDAN BERI DAQIQA.
 * `<08:00`, `08:00`, `08:15`, …, `09:45`, `10:00+` (15 daqiqalik qadam).
 */
export const ARRIVAL_BUCKETS: BucketSet = {
	edges: kelishChegaralari,
	labels: [
		`<${soatMatni(KELISH_BOSHI)}`,
		...kelishChegaralari.slice(0, -1).map(soatMatni),
		`${soatMatni(KELISH_OXIRI)}+`,
	],
};

/**
 * `daily` qatorlarini tartiblaydi va bir sanadagi takrorlarni qo'shadi.
 *
 * ⚠️ Bo'sh kun QO'SHILMAYDI — kunlar SQL dan keladi. `dates` bo'sh
 * bo'lmasa, natija shu tartibda chiqadi (`spark[].points` bilan
 * hizalash uchun); `dates` da yo'q sanadagi qator TASHLANMAYDI —
 * o'sish tartibida oxiriga qo'shiladi. `dates` bo'sh bo'lsa — sana
 * bo'yicha o'sish tartibi.
 */
export function fillDaily(dates: string[], rows: ReportAnalyticsDaily[]): ReportAnalyticsDaily[] {
	const jamlangan = new Map<string, ReportAnalyticsDaily>();
	for (const row of rows) {
		const mavjud = jamlangan.get(row.date);
		if (mavjud) {
			mavjud.primary += row.primary;
			mavjud.flagged += row.flagged;
		} else {
			jamlangan.set(row.date, { date: row.date, primary: row.primary, flagged: row.flagged });
		}
	}

	const natija: ReportAnalyticsDaily[] = [];
	for (const sana of dates) {
		const row = jamlangan.get(sana);
		if (row) {
			natija.push(row);
			jamlangan.delete(sana);
		}
	}

	const qolgan = [...jamlangan.values()].sort((a, b) =>
		a.date < b.date ? -1 : a.date > b.date ? 1 : 0
	);
	return natija.concat(qolgan);
}

/**
 * Foiz, 0..100, bir kasr xonasi. `denominator === 0` → `null`
 * («0%» — yolg'on ayblov). ⚠️ Frontend `davomatFoizi` 0..1 ULUSH
 * qaytaradi — bu boshqa birlik, birlashtirmang.
 */
export function rate(numerator: number, denominator: number): number | null {
	if (denominator === 0) {
		return null;
	}
	return Math.round((numerator / denominator) * 1000) / 10;
}
